import { takeLatest, takeEvery, call, put } from 'redux-saga/effects'
import { delay } from 'redux-saga'
import * as actionTypes from '../modules/actions'
import api from '../modules/api'
import auth from '../modules/auth'
import { mapAddressParser, excludePrefecture, convertLineBreaksToHtml, getDataType, getActiveItemFromId } from '../modules/formatting'
import _ from 'lodash'
import {initialState} from '../routes/signup/modules/signup'
import moment from 'moment'

// worker saga: makes the api call when watcher saga sees the action
export function* signupWorkerData() {
  try {
    const response = yield call(api.fetchTerms);
    const term = convertLineBreaksToHtml(response.data.term.body)

    // fetch prefectures
    const responsePrefectures = yield call(api.fetchPrefectures)
    let prefectures = []
    if (responsePrefectures.data.prefectures) {
      prefectures = responsePrefectures.data.prefectures.filter(excludePrefecture)
    }
    const firstPrefecture = prefectures[0] || {}

    // fetch addresses
    const responseAddresses = yield call(api.fetchAddressesByPrefectures, [firstPrefecture.id])
    let addresses = []
    if (responseAddresses.data.contents) {
      addresses = responseAddresses.data.contents.map(mapAddressParser)
    }
    const firstAddress = addresses[0] || {}

    yield put({
      type: actionTypes.UPDATE_SIGNUP_DATA,
      payload: {
        terms: term,
        prefectures: prefectures,
        addresses: addresses,
        prefectureId: firstPrefecture.id,
        addressId: firstAddress.id
      }
    })
  } catch (error) {
  }
}

export function* signupWorkerFetchAddresses({ prefectures, target, addressIdTarget }) {
  try {
    const response = yield call(api.fetchAddressesByPrefectures, prefectures)
    let addresses = []
    if (response.data.contents) {
      addresses = response.data.contents.map(mapAddressParser)
    }
    const firstAddress = addresses[0] || {}

    const payload = {}
    payload[target] = addresses
    payload[addressIdTarget] = firstAddress.id
    yield put({
      type: actionTypes.UPDATE_SIGNUP_DATA,
      payload
    })
  } catch (error) {
  }
}

export function* signupWorkerSubmit(action) {
  try {
    const response = yield call(api.signup, action.info)
    action.successCallback(response.data)
  } catch (error) {
    action.failCallback(error.response.data)
  }
}

export function* signupWorkerSavePassword(action) {
  try {
    const id = auth.getCustomerInfoBy('id')
    const response = yield call(api.updateCustomerInfo, id, action.info)
    action.successCallback(response.data)
  } catch (error) {
    action.failCallback(error.response.data)
  }
}

export function* signupWorkerSendSMS(action) {
  try {
    const id = auth.getCustomerInfoBy('id')
    const response = yield call(api.sendSMS, id, action.info)
    action.successCallback(response.data)
  } catch (error) {
    action.failCallback(error.response.data)
  }
}

export function* signupWorkerReceiveAuthCode(action) {
  try {
    const id = auth.getCustomerInfoBy('id')
    const response = yield call(api.receiveAuthCode, id, action.info)
    action.successCallback(response.data)
  } catch (error) {
    action.failCallback(error.response.data)
  }
}

export function* signupWorkerUpdateRequestInfo(action) {
  if(action.delay) {
    yield delay(action.delay)
  }
  try {
    const response = yield call(api.updateRequestInfo, action.id, action.data)
    if (response.data.customer) {
      auth.updateCustomerInfo(response.data.customer)
    }
    action.successCallback(response.data)
  } catch (error) {
  }
}

export function* signupWorkerSearchHospital(action) {
  try {
    const response = yield call(api.searchHospital, action.keyword)
    let hospitals = []
    if (response.data.hospitals) {
      hospitals = response.data.hospitals.map(hospital => ({
        ...hospital,
        id: hospital.hospital_id,
        name: hospital.hospital_name
      }))
    }
    yield put ({
      type: actionTypes.UPDATE_SIGNUP_DATA,
      payload: {
        hospitals: hospitals
      }
    })
  } catch (error) {
  }
}

function makeRequestInfoPayload(data) {
  let info = data.info ? data.info : {}
  // minDate + 7 days
  let defaultExpirationData = moment(data.expiration_date_limit.min_date).add(7, 'days').format("YYYY-MM-DD")
  const maxDate = moment(data.expiration_date_limit.max_date)
  // maxDate がデフォルト選択日より過去日の場合、maxDateをデフォルト選択日とする
  if (maxDate.isValid() && maxDate.isBefore(defaultExpirationData)) {
    defaultExpirationData = maxDate
  }
  let hasPassword = auth.getInfo().customer.initial_password_set
  let consultationOption = info.consultation_option ?
    _.compact(_.split(info.consultation_option, ','))
    : data.consultation_option || ''
  let diseaseName = info.disease_name ?
    _.toString(info.disease_name)
    : data.icd10 || ''
  return {
    findmeId: data.findme_id,
    isSavedByCustomer: data.is_saved_by_customer,
    isSentRequest: info.is_sent_request || data.is_saved_by_customer,
    requestId: data.id,
    patientType: data.patient_type,
    patientConsent: info.patient_consent || false,
    prefectureId: _.toString(data.prefecture_id),
    expirationDate: _.toString(data.expiration_date).substr(0, 10) || defaultExpirationData, // get 10 first elements '2018-02-12T18:26:51.093+09:00'
    expirationDateMin: _.toString(data.expiration_date_limit.min_date),
    expirationDateMax: _.toString(data.expiration_date_limit.max_date),
    doctorRequestLimitDatetime: data.doctor_request_limit_datetime,
    files: data.files,
    status: data.status,
    editable: data.editable,
    address: data.address,
    updatedAt: data.updated_at,
    chatroomId: data.findme_chatroom_id,
    // request_info.info
    currentQuestion: info.current_question ? _.toNumber(info.current_question) : 0,
    step: info.current_step ? _.toNumber(info.current_step) : hasPassword ? 3 : 2,
    reachedStep: info.reached_step ? _.toNumber(info.reached_step) : 1,
    title: info.title,
    diseaseName: diseaseName,
    diseaseNameOther: _.toString(info.disease_name_other),
    otherDiseaseNames: _.compact(_.split(info.other_disease_names, ',')),
    otherDiseaseNamesOther: _.toString(info.other_disease_names_other),
    icd10: info.icd_10_code,
    birthYear: info.birth_year || initialState.birthYear,
    cancerStep: info.cancer_step,
    cancerStepOther: info.cancer_step_other,
    cancerType: info.cancer_type,
    consultationOption: consultationOption,
    consultationOptionOther: _.toString(info.consultation_option_other),
    answer1Description: _.toString(info.answer1_description),
    answer2Description: _.toString(info.answer2_description),
    answer3Description: _.toString(info.answer3_description),
    answer4Description: _.toString(info.answer4_description),
    wantTransfer: info.want_transfer, // 転院希望
    offerArea: _.compact(_.split(info.offer_area, ',')),
    hospitalId: info.hospital_id,
    hospitalName: _.toString(info.hospital_name),
    expirationDateComment: _.toString(info.expiration_date_comment),
    contentConsultation: _.toString(info.content),
    documentState: info.document_state,
    otherDocumentState: info.other_document_state,
    submissionMethod: info.submission_method,
    campaignCode: _.toString(info.campaign_from),
    returnAddress: {
      name: info.return_address_name,
      postcode1: info.return_address_postcode1,
      postcode2: info.return_address_postcode2,
      address1: info.return_address_address1,
      address2: info.return_address_address2,
      address3: info.return_address_address3,
      street: info.return_address_street,
      phoneNumber: info.return_address_phoneNumber,
    },
    campaign: info.custom_campaign,
    supportExtra: info.support_extra,
    requiredQuestionAnsweredAt: info.required_question_answered_at,
    optionalQuestionAnsweredAt: info.optional_question_answerd_at,
  }
}


const customerData = () => {
  let { customer } = auth.getInfo()

  let ret = {
    customer_firstName: customer.first_name || '',
    customer_lastName: customer.last_name || '',
    customer_firstNameReading: customer.first_name_reading || '',
    customer_lastNameReading: customer.last_name_reading || '',
    customer_gender: customer.gender || '',
    customer_age: customer.age || '',
    customer_addressId: customer.municipality_id || '',
    customer_prefectureId: customer.prefecture_id || '',
    customer_restAddress: customer.rest_address || '',
    customer_sourceOption: customer.source_option || '',
    customer_sourceCode: customer.source_code || '',
  }
  return ret
}

export function* signupWorkerFetchRequestInfo() {
  try {
    const response = yield call(api.fetchRequestInfo)
    if (response.data.request) {
      let data = response.data.request
      let info = data.info ? data.info : {}

      sessionStorage.is_aflac = info.custom_campaign === 'AFLAC' ? "true" : "false"

      let payload = makeRequestInfoPayload(data)
      let initialCustomerData = customerData()

      // fetch price list
      const priceResponse = yield call(api.fetchPrices, [data.id])
      let prices = priceResponse.data ? priceResponse.data.data : {}

      // fetch prefectures
      const responsePrefectures = yield call(api.fetchPrefectures)
      let prefectures = []
      if (responsePrefectures.data.prefectures) {
        prefectures = responsePrefectures.data.prefectures.filter(excludePrefecture)
      }
      const firstPrefecture = prefectures[0] || {}
      const firstPrefectureId = data.prefecture_id ? data.prefecture_id : firstPrefecture.id
      const firstCustomerPrefectureId = initialCustomerData.customer_prefectureId ? initialCustomerData.customer_prefectureId : firstPrefecture.id

      // fetch addresses
      const responseAddresses = yield call(api.fetchAddressesByPrefectures, [firstPrefectureId])
      let addresses = []
      if (responseAddresses.data.contents) {
        addresses = responseAddresses.data.contents.map(mapAddressParser)
      }
      const firstAddress = addresses[0] || {}
      const firstAddressId = data.municipality_id ? data.municipality_id : firstAddress.id

      // fetch addresses for customer
      const responseCustomerAddresses = yield call(api.fetchAddressesByPrefectures, [firstCustomerPrefectureId])
      let customer_addresses = []
      if (responseCustomerAddresses.data.contents) {
        customer_addresses = responseCustomerAddresses.data.contents.map(mapAddressParser)
      }
      const firstCustomerAddress = customer_addresses[0] || {}
      const firstCustomerAddressId = initialCustomerData.customer_addressId ? initialCustomerData.customer_addressId : firstCustomerAddress.id

      const directoryUploads = {}
      if (data.findme_directory_uploads_count > 0) {
        const directoryUploadsRaw = yield call(api.listDirectoryUploads, data.id)
        const directoryUploadsRawData = directoryUploadsRaw.data.directory_uploads
        for (let i = directoryUploadsRawData.length - 1; i >= 0; i--) {
          let du = directoryUploadsRawData[i]
          directoryUploads[du.id] = {
            id: du.id,
            fileCount: du.total_file_count,
            totalFileSize: du.total_directory_size,
            finalized: true
          }
        }
      }

      // check budget in budgets array or not
      let budget = _.toString(data.budget)
      let budgetId = getActiveItemFromId(initialState.budgets, budget, 'id')

      // check number hospital in numberHospitals array or not
      let numberHospital = Number(info.number_of_hospital)
      let numberHospitalId = getActiveItemFromId(initialState.numberHospitals, numberHospital, 'id')
      yield put({
        type: actionTypes.UPDATE_SIGNUP_DATA,
        payload: {
          ...payload,
          ...initialCustomerData,
          isFetchedRequestData: true,
          // for special select fields depend on api data
          prefectures,
          addresses,
          customer_addresses,
          prefectureId: firstPrefectureId,
          addressId: firstAddressId,
          customer_prefectureId: firstCustomerPrefectureId,
          customer_addressId: firstCustomerAddressId,
          cancerStep: info.cancer_step ?_.toString(info.cancer_step) : initialState.cancerStep,
          cancerType: info.cancer_type ?_.toString(info.cancer_type) : initialState.cancerType,
          budget: budgetId ? budgetId : initialState.budget,
          numberHospital: numberHospitalId ? numberHospitalId : initialState.numberHospital,
          prices,
          directoryUploads,
          directoryUploadsCount: Object.keys(directoryUploads).length,
        }
      })
      yield put({
        type: actionTypes.UPDATE_QUESTIONS,
      })
    } // endif
  } catch (error) {
  }
}

export function* signupWorkerFetchConsultationOptions() {
  try {
    const response = yield call(api.fetchConsultationOptions)
    if (response.data.data) {
      let data = response.data.data.map(item => ({
        id: item,
        name: item
      }))
      yield put({
        type: actionTypes.UPDATE_SIGNUP_DATA,
        payload: {
          consultationOptions: data,
        }
      })
    }
  } catch (error) {
  }
}

export function* signupWorkerUploadFiles(action) {
  try {
    const response = yield call(api.uploadFiles, action.data)
    action.successCallback(response.data)
  } catch (error) {
  }
}

export function* signupWorkerStartUploadDirectory(action) {
  try {
    const data = {
      file_count: action.fileCount,
      total_size: action.totalFileSize,
    }
    const response = yield call(api.uploadDirectory, action.requestId, data)
    action.successCallback(response.data)
  } catch (error) {
  }
}

export function* signupWorkerUploadFileToS3({url, fields, onUploadProgress, successCallback}) {
  try {
    yield call(api.uploadFileToS3, url, fields, onUploadProgress)
    successCallback()
  } catch (error) {
  }
}

export function* signupWorkerUpdateFileTargetId(action) {
  try {
    yield call(api.updateFileTargetId, action.fileId, action.targetId)
  } catch (error) {
  }
}

export function* signupWorkerDeleteFile(action) {
  try {
    const response = yield call(api.deleteFile, action.fileId)
    action.successCallback(response.data)
  } catch (error) {
  }
}

export function* signupWorkerDeleteDirectoryUpload(action) {
  try {
    const response = yield call(api.deleteDirectoryUpload, action.requestId, action.directoryUploadId)
    action.successCallback(response.data)
  } catch (error) {
  }
}

export function* signupWorkerFetchPayjpInfo(action) {
  try {
    const response = yield call(api.fetchPayjpInfo, action.token)
    yield put({
      type: actionTypes.UPDATE_SIGNUP_DATA,
      payload: {
        payjpInfo: response.data.data
      }
    })
  } catch (error) {
  }
}

export function* signupWorkerFetchAnnotationPrice({ requestId }) {
  try {
    const response = yield call(api.fetchAnnotationPrice, requestId)
    yield put({
      type: actionTypes.UPDATE_SIGNUP_DATA,
      payload: {
        annotationPrice: response.data.data
      }
    })
  } catch (error) {
  }
}

export function* signupWorkerFetchDataFile() {
  try {
    const response = yield call(api.fetchRequestInfo)

    // fetch price list
    let requestId = null

    let files = []
    let fileData = {}
    if (response.data.request) {
      let data = response.data.request
      requestId = data.id
      files = data.files
      let viewableFiles = []
      let documentFiles = []
      files.forEach(function(item) {
        let filterFile = item.cleaned ? item.cleaned_file_name : item.original_file_name
        let dataType = getDataType(filterFile)
        if(dataType === 'image' || dataType === 'pdf') {
          item.type = dataType
          viewableFiles.push(item)
        } else {
          documentFiles.push(item)
        }
      })

      fileData = {
        viewableFiles,
        documentFiles,
      }

      const responsePrefectures = yield call(api.fetchPrefectures)
      let prefectures = []
      if (responsePrefectures.data.prefectures) {
        prefectures = responsePrefectures.data.prefectures.filter(excludePrefecture)
      }

      const directoryUploads = {}
      if (data.findme_directory_uploads_count > 0) {
        const directoryUploadsRaw = yield call(api.listDirectoryUploads, data.id)
        const directoryUploadsRawData = directoryUploadsRaw.data.directory_uploads
        for (let i = directoryUploadsRawData.length - 1; i >= 0; i--) {
          let du = directoryUploadsRawData[i]
          directoryUploads[du.id] = {
            id: du.id,
            fileCount: du.total_file_count,
            totalFileSize: du.total_directory_size,
            finalized: true
          }
        }
      }

      yield put({
        type: actionTypes.UPDATE_SIGNUP_DATA,
        payload: {
          prefectures: prefectures,
          ...makeRequestInfoPayload(data),
          directoryUploads,
          directoryUploadsCount: Object.keys(directoryUploads).length,
        },
      })
      yield put({
        type: actionTypes.UPDATE_QUESTIONS,
      })
    }

    const priceResponse = yield call(api.fetchPrices, [requestId])
    let prices = priceResponse.data ? priceResponse.data.data : {}

    yield put({
      type: actionTypes.UPDATE_SIGNUP_DATA,
      payload: {
        prices: prices,
      },
    })

    for(let j = 0; j < fileData.viewableFiles.length; j++) {
      const file = fileData.viewableFiles[j]
      const fileReponse = yield call(
        api.fetchFileURL,
        file.id,
        file.cleaned ? "cleaned" : "original"
      )
      let imgSrc = fileReponse.data.url
      let requiresAuth = fileReponse.data.requires_auth
      // TODO: Remove this after all Rails-encrypted files are
      // removed from the system.
      if (requiresAuth) {
        var fileDataReponse = yield call(
          api.fetchFileData,
          file.id,
          file.cleaned ? "cleaned" : "original"
        )
        var contentType = fileDataReponse.headers["content-type"]
        var uInt8Array = new Uint8Array(fileDataReponse.data)
        var i = uInt8Array.length
        var binaryString = new Array(i)
        while (i--) {
          binaryString[i] = String.fromCharCode(uInt8Array[i])
        }
        var data = binaryString.join('')
        var base64 = btoa(data)
        imgSrc = 'data:'+contentType+';base64,' + base64
      }
      fileData.viewableFiles[j] = {
        ...file,
        'src': imgSrc,
      }
    }

    yield put({
      type: actionTypes.UPDATE_SIGNUP_DATA,
      payload: {
        fileData: fileData,
      }
    })
  } catch (error) {
  }
}

// @see https://github.com/redux-saga/redux-saga/issues/160
export default [
  takeLatest(actionTypes.FETCH_SIGNUP_DATA_REQUEST, signupWorkerData),
  takeLatest(actionTypes.FETCH_SIGNUP_ADDRESSES_REQUEST, signupWorkerFetchAddresses),
  takeLatest(actionTypes.SIGNUP_REQUEST, signupWorkerSubmit),
  takeLatest(actionTypes.SIGNUP_SAVE_PASSWORD_REQUEST, signupWorkerSavePassword),
  takeLatest(actionTypes.SEND_SMS_REQUEST, signupWorkerSendSMS),
  takeLatest(actionTypes.RECEIVE_SMS_AUTH_CODE_REQUEST, signupWorkerReceiveAuthCode),
  takeLatest(actionTypes.UPDATE_REQUEST_INFO_REQUEST, signupWorkerUpdateRequestInfo),

  // Each file will be downloaded individually, so takeEvery is used
  takeEvery(actionTypes.UPLOAD_FILE_REQUEST, signupWorkerUploadFiles),
  takeEvery(actionTypes.UPLOAD_FILE_S3_REQUEST, signupWorkerUploadFileToS3),
  takeEvery(actionTypes.UPDATE_FILE_TARGET_ID_REQUEST, signupWorkerUpdateFileTargetId),

  // Directory uploading
  // Only one directory can be uploaded at a time.
  takeLatest(actionTypes.UPLOAD_DIRECTORY_REQUEST, signupWorkerStartUploadDirectory),

  takeLatest(actionTypes.FETCH_REQUEST_INFO_REQUEST, signupWorkerFetchRequestInfo),
  takeLatest(actionTypes.SEARCH_HOSPITAL_REQUEST, signupWorkerSearchHospital),
  takeLatest(actionTypes.FETCH_CONSULTATION_OPTIONS_REQUEST, signupWorkerFetchConsultationOptions),
  takeLatest(actionTypes.DELETE_FILE_REQUEST, signupWorkerDeleteFile),
  takeLatest(actionTypes.DELETE_DIRECTORY_UPLOAD_REQUEST, signupWorkerDeleteDirectoryUpload),
  takeLatest(actionTypes.FETCH_PAYJP_INFO_REQUEST, signupWorkerFetchPayjpInfo),
  takeLatest(actionTypes.FETCH_ANNOTATION_PRICE_REQUEST, signupWorkerFetchAnnotationPrice),
  takeLatest(actionTypes.FETCH_DATA_FILE_REQUEST, signupWorkerFetchDataFile),
]



// WEBPACK FOOTER //
// ./src/sagas/signupSagas.js